import Link from '@/routing/AppLink';
import { useRouter } from '@/routing/useAppRouter';
import type { LinkProps } from 'next/link';
import 'swiper/css';
import { FreeMode } from 'swiper/modules';
import {
  Swiper as ReactSwiper,
  SwiperSlide as ReactSwiperSlide,
} from 'swiper/react';
import { UrlObject } from 'url';

import { IconArrowUpRightFromSquareRound } from '@/components/apps/ui/icon';

import BadgeNoti from '../badge/BadgeNoti';

type SizeType = 'sm' | 'md';

type NextLinkProps = Pick<LinkProps, 'replace' | 'scroll'>;

export type TabItemType = {
  key: string;
  label: React.ReactNode;
  badgeCount?: number;
  href: UrlObject | string;
  target?: '_blank' | '_self';
};

interface TabTextProps extends NextLinkProps {
  size?: SizeType;
  className?: string;
  hasInnerArea?: boolean;
  isCenteredSlides?: boolean;
  items: TabItemType[];
  itemsGap?: number;
  onChange?: (item: TabItemType) => void;
}

export default function TabText({
  size = 'md',
  className,
  hasInnerArea = false,
  isCenteredSlides = false,
  items,
  itemsGap = 32,
  onChange,
  ...props
}: TabTextProps) {
  const router = useRouter();

  const activeTabKey =
    router.query.termType?.toString() ||
    router.query.category?.toString() ||
    router.query.board?.toString() ||
    items[0].key;

  const handleOnChange = (item: any) => {
    onChange?.(item);
  };

  return (
    <div
      className={`flex w-full justify-center border-b border-solid border-apps-coreProductDivider ${className ?? ''}`}
    >
      <ReactSwiper
        className={`m-0 max-w-full flex-1 ${hasInnerArea ? 'px-gutter' : ''}`}
        modules={[FreeMode]}
        slidesPerView="auto"
        spaceBetween={itemsGap}
        centerInsufficientSlides={isCenteredSlides} // 슬라이드의 양이 slidePerView보다 적은 경우 중앙 슬라이드 정렬
        freeMode
        grabCursor
        scrollbar={{ draggable: true }}
        initialSlide={
          activeTabKey
            ? items.findIndex((item) => item.key === activeTabKey)
            : 0
        }
      >
        {items.map((item) => (
          <ReactSwiperSlide key={item.key} className="w-max">
            <Link
              {...props}
              href={item.href as UrlObject}
              target={item.target}
              className={tabItemClass(size, item.key === activeTabKey)}
              onClick={() => handleOnChange(item)}
            >
              {item.label}

              {item.target !== '_blank' && !!item.badgeCount && (
                <span className="ml-1.5">
                  <BadgeNoti badgeContent={item.badgeCount} color="primary" />
                </span>
              )}

              {item.target === '_blank' && (
                <span className="ml-0.5">
                  <IconArrowUpRightFromSquareRound size={16} />
                </span>
              )}
            </Link>
          </ReactSwiperSlide>
        ))}
      </ReactSwiper>
    </div>
  );
}

/**
 * size 는 font-weight 를 normal 로, active 는 bold 로 준다.
 * 유틸리티가 전부 !important 라 둘을 함께 붙이면 CSS 출력 순서로 승자가 정해지므로
 * 굵기는 한 번만 결정해서 붙인다.
 */
const SIZE_CLASS: Record<SizeType, string> = {
  sm: 'h-[30px] pb-[9px] text-sm leading-[150%] after:h-0.5',
  md: 'h-9 pb-[14px] text-lg leading-[120%] after:h-[3px]',
};

function tabItemClass(size: SizeType, isActive: boolean) {
  return [
    'flex w-fit items-center whitespace-nowrap text-apps-coreGrayBlack',
    SIZE_CLASS[size],
    isActive
      ? "font-bold after:absolute after:bottom-0 after:left-0 after:w-full after:bg-apps-corePrimary01 after:content-['']"
      : 'font-normal',
  ].join(' ');
}
